import React from "react";
import { motion } from "framer-motion";
import { Eye, Network, Workflow, Maximize2, Target } from "lucide-react";
import { useLang } from "../../contexts/LanguageContext";

const ICONS = [Eye, Network, Workflow, Maximize2, Target];

export default function WhyUs() {
  const { t } = useLang();

  return (
    <section
      id="why"
      data-testid="why-section"
      className="relative py-24 md:py-32 bg-[#0B1120] text-white overflow-hidden"
    >
      {/* Decorative grid */}
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
        aria-hidden="true"
      />
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[40rem] h-[40rem] rounded-full bg-[radial-gradient(closest-side,rgba(30,91,214,0.35),transparent_70%)] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase font-bold text-[#7BA7FF]">
            <span className="h-px w-8 bg-[#7BA7FF]" />
            {t.why.label}
          </div>
          <h2
            data-testid="why-title"
            className="mt-5 font-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]"
          >
            {t.why.title}
          </h2>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 rounded-sm overflow-hidden">
          {t.why.items.map((item, i) => {
            const Icon = ICONS[i] || Network;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.07 }}
                data-testid={`why-item-${i}`}
                className="group relative bg-[#0B1120] p-8 lg:p-10 hover:bg-[#0E1730] transition-colors duration-300"
              >
                <div className="flex items-center gap-4">
                  <span className="font-heading text-3xl font-extrabold text-white/30 group-hover:text-[#7BA7FF] transition-colors">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-sm bg-white/5 border border-white/10 group-hover:border-[#1E5BD6] transition-colors">
                    <Icon className="h-4 w-4 text-[#7BA7FF]" />
                  </span>
                </div>
                <h3 className="mt-6 font-heading text-xl font-bold tracking-tight">
                  {item.title}
                </h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{item.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
